import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import useEmblaCarousel from 'embla-carousel-react'
import spotBoxImg from '../../assets/img/spot-box-l.png'
import spotPackageImg from '../../assets/img/spot-package.png'
import spotPresentImg from '../../assets/img/spot-present.png'
import spotProductionImg from '../../assets/img/spot-production.png'
import { useEmblaDots } from '../../hooks/useEmblaDots'
import { Icon } from '../Icon/Icon'
import { SectionHeading } from '../SectionHeading/SectionHeading'
import styles from './SendGrid.module.scss'

interface SendCard {
  bandClass: string
  image: string
  key: string
}

const CARDS: SendCard[] = [
  { bandClass: 'bandAqua', image: spotPresentImg, key: 'gift' },
  { bandClass: 'bandWheat', image: spotBoxImg, key: 'marketplace' },
  { bandClass: 'bandVanilla', image: spotPackageImg, key: 'family' },
  { bandClass: 'bandSteel', image: spotProductionImg, key: 'business' },
]

const MOBILE_ONLY_OPTIONS = {
  active: false,
  align: 'center',
  breakpoints: { '(max-width: 767px)': { active: true } },
  loop: true,
} as const

export const SendGrid = () => {
  const { t } = useTranslation()
  const [emblaRef, emblaApi] = useEmblaCarousel(MOBILE_ONLY_OPTIONS)
  const { onDotClick, selectedIndex } = useEmblaDots(emblaApi)

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  return (
    <section className={styles.section} id="features">
      <div className="container">
        <SectionHeading subtitle={t('send.subtitle')} title={t('send.title')} />
        <div ref={emblaRef} className={styles.viewport}>
          <ul className={styles.list}>
            {CARDS.map((card) => (
              <li key={card.key} className={styles.slide}>
                <div className={styles.card}>
                  <div className={`${styles.picWrap} ${styles[card.bandClass]}`}>
                    <img
                      alt=""
                      className={styles.image}
                      height="180"
                      loading="lazy"
                      src={card.image}
                      width="180"
                    />
                  </div>
                  <div className={styles.caption}>
                    <h3 className={styles.title}>{t(`send.cards.${card.key}.title`)}</h3>
                    <p className={styles.description}>{t(`send.cards.${card.key}.description`)}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.controls}>
          <button
            aria-label={t('howModal.prev')}
            className={styles.arrow}
            type="button"
            onClick={scrollPrev}
          >
            <Icon className={styles.prev} name="chevron-right" size={24} />
          </button>
          <div className={styles.dots}>
            {CARDS.map((card, index) => (
              <button
                key={card.key}
                aria-current={index === selectedIndex}
                aria-label={t('howModal.step', { number: index + 1 })}
                className={
                  index === selectedIndex ? `${styles.dot} ${styles.dotActive}` : styles.dot
                }
                type="button"
                onClick={() => {
                  onDotClick(index)
                }}
              />
            ))}
          </div>
          <button
            aria-label={t('howModal.next')}
            className={styles.arrow}
            type="button"
            onClick={scrollNext}
          >
            <Icon name="chevron-right" size={24} />
          </button>
        </div>
      </div>
    </section>
  )
}
